import { useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { safeGetAccessToken } from "../utils/auth";
import { usePlayground } from '../contexts/PlaygroundContext';
import { API_BASE } from '../shared/Constants';
import { Message } from '../components/Playground/types';
import { reportApiError, reportHandledError } from "../utils/reportError";

export function usePlaygroundStreaming() {
    const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
    const { state, dispatch } = usePlayground();
    const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

    const buildMessages = useCallback((systemPrompt: string, userMessage: string): Message[] => {
        const messages: Message[] = [];

        if (systemPrompt.trim()) {
            messages.push({
                role: 'system',
                content: systemPrompt.trim(),
            });
        }

        messages.push({
            role: 'user',
            content: userMessage.trim(),
        });

        return messages;
    }, []);

    const streamModelResponse = useCallback(async (
        promptIndex: number,
        modelId: string,
        token: string,
        messages: Message[],
        signal: AbortSignal
    ) => {
        dispatch({ type: 'START_GENERATION', promptIndex, modelId });

        try {
            const response = await fetch(`${API_BASE}/api/chat/completions`, {
                method: 'POST',
                headers: {
                    'Authorization': `Auth ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    model: modelId,
                    messages,
                    max_tokens: state.parameters.maxTokens,
                    temperature: state.parameters.temperature,
                    top_p: state.parameters.topP,
                    stop: state.parameters.stoppingTokens.length > 0
                        ? state.parameters.stoppingTokens
                        : undefined,
                    stream: true,
                    stream_options: { include_usage: true },
                }),
                signal,
            });

            if (!response.ok) {
                reportApiError(response, { feature: "playground.stream.completion", method: "POST" });
                const errorText = await response.text();
                let errorMessage = `Erro ${response.status}`;
                try {
                    const errorJson = JSON.parse(errorText);
                    errorMessage = errorJson.error?.message || errorJson.message || errorMessage;
                } catch {
                    errorMessage = errorText || errorMessage;
                }
                throw new Error(errorMessage);
            }

            const reader = response.body?.getReader();
            if (!reader) {
                throw new Error('Stream nao disponivel');
            }

            const decoder = new TextDecoder();
            let buffer = '';
            let totalInputTokens = 0;
            let totalOutputTokens = 0;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    const trimmedLine = line.trim();
                    if (!trimmedLine || !trimmedLine.startsWith('data: ')) continue;

                    const jsonStr = trimmedLine.slice(6);
                    if (jsonStr === '[DONE]') {
                        continue;
                    }

                    try {
                        const parsed = JSON.parse(jsonStr);
                        const content = parsed.choices?.[0]?.delta?.content;

                        if (content) {
                            dispatch({ type: 'APPEND_CONTENT', promptIndex, modelId, content });
                        }

                        if (parsed.usage) {
                            totalInputTokens = parsed.usage.prompt_tokens || totalInputTokens;
                            totalOutputTokens = parsed.usage.completion_tokens || totalOutputTokens;
                        }
                    } catch {
                        // Skip malformed JSON chunks
                    }
                }
            }

            dispatch({
                type: 'COMPLETE_GENERATION',
                promptIndex,
                modelId,
                tokenCount: { input: totalInputTokens, output: totalOutputTokens }
            });
        } catch (error) {
            if ((error as Error).name === 'AbortError') {
                dispatch({ type: 'COMPLETE_GENERATION', promptIndex, modelId });
                return;
            }
            reportHandledError(error, { category: "sse", feature: "playground.stream.completion" });
            dispatch({
                type: 'GENERATION_ERROR',
                promptIndex,
                modelId,
                error: (error as Error).message || 'Erro desconhecido'
            });
        }
    }, [dispatch, state.parameters]);

    const generateResponses = useCallback(async () => {
        // Get prompts with content
        const promptsWithContent = state.userMessages
            .map((userMessage, index) => ({
                userMessage,
                systemPrompt: state.systemPrompts[index] || '',
                index
            }))
            .filter(({ userMessage }) => userMessage.trim().length > 0);

        if (state.selectedModels.length === 0 || promptsWithContent.length === 0) {
            return;
        }

        // Clear previous responses and abort any ongoing requests
        abortControllersRef.current.forEach(controller => controller.abort());
        abortControllersRef.current.clear();

        dispatch({ type: 'CLEAR_RESPONSES' });
        dispatch({ type: 'SET_IS_GENERATING', isGenerating: true });

        try {
            const token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
            if (!token) return;

            // Create abort controllers for each prompt-model combination
            for (const { index: promptIndex } of promptsWithContent) {
                for (const modelId of state.selectedModels) {
                    const key = `${promptIndex}-${modelId}`;
                    abortControllersRef.current.set(key, new AbortController());
                }
            }

            // Start all streams in parallel
            const streamPromises: Promise<void>[] = [];

            for (const { userMessage, systemPrompt, index: promptIndex } of promptsWithContent) {
                const messages = buildMessages(systemPrompt, userMessage);

                for (const modelId of state.selectedModels) {
                    const key = `${promptIndex}-${modelId}`;
                    const controller = abortControllersRef.current.get(key);
                    if (!controller) continue;

                    streamPromises.push(
                        streamModelResponse(promptIndex, modelId, token, messages, controller.signal)
                    );
                }
            }

            await Promise.allSettled(streamPromises);
        } catch (error) {
            reportHandledError(error, { category: "auth", feature: "playground.stream.token" });
            console.error('Error generating responses:', error);
        } finally {
            dispatch({ type: 'SET_IS_GENERATING', isGenerating: false });
            abortControllersRef.current.clear();
        }
    }, [
        state.selectedModels,
        state.userMessages,
        state.systemPrompts,
        getAccessTokenSilently, loginWithRedirect,
        buildMessages,
        streamModelResponse,
        dispatch
    ]);

    const stopGeneration = useCallback(() => {
        abortControllersRef.current.forEach(controller => controller.abort());
        abortControllersRef.current.clear();
        dispatch({ type: 'SET_IS_GENERATING', isGenerating: false });
    }, [dispatch]);

    return {
        generateResponses,
        stopGeneration,
        abortControllers: abortControllersRef.current,
    };
}
